import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import {
  Form,
  FormField,
  Input,
  ModalDialog,
  Numeric,
  Select,
  Table,
  TableCell,
  TableHeadCell,
  TableRow,
  Textarea,
  TranslatableField,
} from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import useImageUploader from "../../../../components/ui/ImageUploader";
import VisibilityScope, { VisibilityScopeValue } from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import {
  CONNECTION_TYPES,
  CompatibilityLevel,
  ConnectionType,
  ConnectionTypeCompatibility,
  SUBSTRATE_CATEGORIES,
  SubstrateCategory,
  SubstrateCompatibility,
  THREAD_TYPES,
  ThreadType,
} from "../../../../http/screwConnector/types/common";
import { InsertScrewFamilyBody, ScrewFamily, UpdateScrewFamilyBody } from "../../../../http/screwConnector/types/screwFamilies";
import useScrewConnectorScrewFamiliesApi from "../../../../http/screwConnector/useAdminScrewFamilies";
import { translatableFieldProps } from "../../../../utils/translatableField";
import useI18n from "../../../../hooks/useTranslations";
import CompatibilityLevelEditor from "./CompatibilityLevelEditor";

interface ScrewFamilyFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  threadType: ThreadType | null;
  material: string | null;
  materialIt: string | null;
  materialDe: string | null;
  materialEs: string | null;
  materialFr: string | null;
  coating: string | null;
  coatingIt: string | null;
  coatingDe: string | null;
  coatingEs: string | null;
  coatingFr: string | null;
  ccCode: string | null;
  tcCode: string | null;
  scEuCode: string | null;
  ecUsCode: string | null;
  wdUsCode: string | null;
  standardReference: string | null;
  visibility: VisibilityScopeValue;
  order: number | null;
  connectionTypes: ConnectionTypeCompatibility[];
  substrates: SubstrateCompatibility[];
}

interface ScrewFamilyFormProps {
  screwFamily?: ScrewFamily;
  onClose: () => void;
}

const ScrewFamilyForm: FC<ScrewFamilyFormProps> = ({ screwFamily, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const dispatch = useContext(NotificationDispatch);
  const screwFamiliesApi = useScrewConnectorScrewFamiliesApi();

  const threadTypeLabels: Record<ThreadType, string> = {
    partial: t.threadTypes.partial,
    full: t.threadTypes.full,
  };

  const connectionTypeLabels: Record<ConnectionType, string> = {
    shear_screws: t.connectionTypes.shear_screws,
    steel_to_timber: t.connectionTypes.steel_to_timber,
    timber_to_timber: t.connectionTypes.timber_to_timber,
  };

  const substrateCategoryLabels: Record<SubstrateCategory, string> = {
    solidwood_softwood: t.substrateCategories.solidwood_softwood,
    glulam_softwood: t.substrateCategories.glulam_softwood,
    clt_softwood: t.substrateCategories.clt_softwood,
    lvl_softwood: t.substrateCategories.lvl_softwood,
    solidwood_hardwood: t.substrateCategories.solidwood_hardwood,
    lvl_hardwood: t.substrateCategories.lvl_hardwood,
    panels: t.substrateCategories.panels,
    steel: t.substrateCategories.steel,
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: screwFamily?.image ?? undefined,
    isLoading: false,
  });

  const defaultValues: ScrewFamilyFormBody = {
    name: screwFamily?.name ?? null,
    nameIt: screwFamily?.nameIt ?? null,
    nameDe: screwFamily?.nameDe ?? null,
    nameEs: screwFamily?.nameEs ?? null,
    nameFr: screwFamily?.nameFr ?? null,
    description: screwFamily?.description ?? null,
    descriptionIt: screwFamily?.descriptionIt ?? null,
    descriptionDe: screwFamily?.descriptionDe ?? null,
    descriptionEs: screwFamily?.descriptionEs ?? null,
    descriptionFr: screwFamily?.descriptionFr ?? null,
    threadType: screwFamily?.threadType ?? null,
    material: screwFamily?.material ?? null,
    materialIt: screwFamily?.materialIt ?? null,
    materialDe: screwFamily?.materialDe ?? null,
    materialEs: screwFamily?.materialEs ?? null,
    materialFr: screwFamily?.materialFr ?? null,
    coating: screwFamily?.coating ?? null,
    coatingIt: screwFamily?.coatingIt ?? null,
    coatingDe: screwFamily?.coatingDe ?? null,
    coatingEs: screwFamily?.coatingEs ?? null,
    coatingFr: screwFamily?.coatingFr ?? null,
    ccCode: screwFamily?.ccCode ?? null,
    tcCode: screwFamily?.tcCode ?? null,
    scEuCode: screwFamily?.scEuCode ?? null,
    ecUsCode: screwFamily?.ecUsCode ?? null,
    wdUsCode: screwFamily?.wdUsCode ?? null,
    standardReference: screwFamily?.standardReference ?? null,
    visibility: screwFamily?.visibility ?? "public",
    order: screwFamily?.order ?? null,
    connectionTypes: screwFamily?.connectionTypes ?? [],
    substrates: screwFamily?.substrates ?? [],
  };

  const onSubmit = () => async (formData: ScrewFamilyFormBody) => {
    if (!formData.name || !formData.threadType || !formData.material) {
      return;
    }

    const body: InsertScrewFamilyBody | UpdateScrewFamilyBody = {
      name: formData.name,
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameEs: formData.nameEs || null,
      nameFr: formData.nameFr || null,
      description: formData.description?.trim() || null,
      descriptionIt: formData.descriptionIt || null,
      descriptionDe: formData.descriptionDe || null,
      descriptionEs: formData.descriptionEs || null,
      descriptionFr: formData.descriptionFr || null,
      threadType: formData.threadType,
      material: formData.material,
      materialIt: formData.materialIt || null,
      materialDe: formData.materialDe || null,
      materialEs: formData.materialEs || null,
      materialFr: formData.materialFr || null,
      coating: formData.coating?.trim() || null,
      coatingIt: formData.coatingIt || null,
      coatingDe: formData.coatingDe || null,
      coatingEs: formData.coatingEs || null,
      coatingFr: formData.coatingFr || null,
      ccCode: formData.ccCode?.trim() || null,
      tcCode: formData.tcCode?.trim() || null,
      scEuCode: formData.scEuCode?.trim() || null,
      ecUsCode: formData.ecUsCode?.trim() || null,
      wdUsCode: formData.wdUsCode?.trim() || null,
      standardReference: formData.standardReference?.trim() || null,
      image: newImage?.name ?? (removeImage ? null : (screwFamily?.image ?? null)),
      visibility: formData.visibility,
      order: formData.order ?? 0,
      connectionTypes: formData.connectionTypes,
      substrates: formData.substrates,
    };

    try {
      if (screwFamily) {
        await screwFamiliesApi.uploadImage(screwFamily.id, newImage, removeImage);
        await screwFamiliesApi.updateScrewFamily(screwFamily.id, body);
      } else {
        const { id } = await screwFamiliesApi.insertScrewFamily({ ...body, image: null });
        if (newImage) {
          await screwFamiliesApi.uploadImage(id, newImage, false);
          await screwFamiliesApi.updateScrewFamily(id, { ...body, image: newImage.name });
        }
      }
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screwFamily
          ? `${i18n.translation.common.edit} ${t.screwFamilies.singular}`
          : `${i18n.translation.common.add} ${t.screwFamilies.singular}`
      }
    >
      <Form<ScrewFamilyFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, register, formState: { errors }, reset, watch, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [screwFamily]);

          useEffect(() => {
            register("nameIt");
            register("nameDe");
            register("nameEs");
            register("nameFr");
            register("descriptionIt");
            register("descriptionDe");
            register("descriptionEs");
            register("descriptionFr");
            register("materialIt");
            register("materialDe");
            register("materialEs");
            register("materialFr");
            register("coatingIt");
            register("coatingDe");
            register("coatingEs");
            register("coatingFr");
          }, [register]);

          const values = watch();

          return (
            <FormFieldsContainer fullWidth imageUploader={component}>
              <TranslatableField
                {...translatableFieldProps("name", values, setValue, i18n.translation)}
                dialogTitle={i18n.translation.common.name}
              >
                <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
                  {({ labelId, isOptional, isInvalid }) => (
                    <Input id={labelId} isInvalid={isInvalid} {...register("name", { required: !isOptional, minLength: 1 })} />
                  )}
                </FormField>
              </TranslatableField>
              <TranslatableField
                {...translatableFieldProps("description", values, setValue, i18n.translation)}
                dialogTitle={t.screwFamilies.fields.description}
                type="textarea"
              >
                <FormField label={t.screwFamilies.fields.description} error={errors.description} isOptional>
                  {({ labelId, isInvalid }) => <Textarea id={labelId} isInvalid={isInvalid} {...register("description")} />}
                </FormField>
              </TranslatableField>
              <Controller
                name="threadType"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwFamilies.fields.threadType} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<{ value: ThreadType; label: string }, false>
                        inputId={labelId}
                        options={THREAD_TYPES.map((value) => ({ value, label: threadTypeLabels[value] }))}
                        value={field.value ? { value: field.value, label: threadTypeLabels[field.value] } : null}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        isInvalid={isInvalid}
                        onChange={(option) => field.onChange(option?.value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
              <TranslatableField
                {...translatableFieldProps("material", values, setValue, i18n.translation)}
                dialogTitle={t.screwFamilies.fields.material}
              >
                <FormField label={t.screwFamilies.fields.material} error={errors.material} isOptional={false}>
                  {({ labelId, isOptional, isInvalid }) => (
                    <Input id={labelId} isInvalid={isInvalid} {...register("material", { required: !isOptional, minLength: 1 })} />
                  )}
                </FormField>
              </TranslatableField>
              <TranslatableField
                {...translatableFieldProps("coating", values, setValue, i18n.translation)}
                dialogTitle={t.screwFamilies.fields.coating}
              >
                <FormField label={t.screwFamilies.fields.coating} error={errors.coating} isOptional>
                  {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("coating")} />}
                </FormField>
              </TranslatableField>
              <FormField label={t.screwFamilies.fields.ccCode} error={errors.ccCode} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("ccCode")} />}
              </FormField>
              <FormField label={t.screwFamilies.fields.tcCode} error={errors.tcCode} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("tcCode")} />}
              </FormField>
              <FormField label={t.screwFamilies.fields.scEuCode} error={errors.scEuCode} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("scEuCode")} />}
              </FormField>
              <FormField label={t.screwFamilies.fields.ecUsCode} error={errors.ecUsCode} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("ecUsCode")} />}
              </FormField>
              <FormField label={t.screwFamilies.fields.wdUsCode} error={errors.wdUsCode} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("wdUsCode")} />}
              </FormField>
              <FormField label={t.screwFamilies.fields.standardReference} error={errors.standardReference} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("standardReference")} />}
              </FormField>
              <Controller
                name="visibility"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                    {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                  </FormField>
                )}
              />
              <Controller
                name="order"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        decimalScale={0}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="connectionTypes"
                control={control}
                render={({ field }) => {
                  const levelByType = new Map(field.value.map((c) => [c.connectionType, c.compatibilityLevel]));
                  const updateLevel = (connectionType: ConnectionType, compatibilityLevel: CompatibilityLevel) => {
                    field.onChange(
                      CONNECTION_TYPES.map((type) => ({
                        connectionType: type,
                        compatibilityLevel: type === connectionType ? compatibilityLevel : (levelByType.get(type) ?? 0),
                      })),
                    );
                  };
                  return (
                    <FormField label={t.screwFamilies.fields.connectionTypes}>
                      {() => (
                        <Table>
                          <thead>
                            <TableRow>
                              <TableHeadCell align="left">{t.screwFamilies.fields.connectionTypes}</TableHeadCell>
                              <TableHeadCell align="left"></TableHeadCell>
                            </TableRow>
                          </thead>
                          <tbody>
                            {CONNECTION_TYPES.map((type) => (
                              <TableRow key={type}>
                                <TableCell>{connectionTypeLabels[type]}</TableCell>
                                <TableCell>
                                  <CompatibilityLevelEditor
                                    value={levelByType.get(type) ?? 0}
                                    onChange={(level) => updateLevel(type, level)}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </FormField>
                  );
                }}
              />
              <Controller
                name="substrates"
                control={control}
                render={({ field }) => {
                  const levelByCategory = new Map(field.value.map((s) => [s.substrateCategory, s.compatibilityLevel]));
                  const updateLevel = (substrateCategory: SubstrateCategory, compatibilityLevel: CompatibilityLevel) => {
                    field.onChange(
                      SUBSTRATE_CATEGORIES.map((category) => ({
                        substrateCategory: category,
                        compatibilityLevel: category === substrateCategory ? compatibilityLevel : (levelByCategory.get(category) ?? 0),
                      })),
                    );
                  };
                  return (
                    <FormField label={t.screwFamilies.fields.substrates}>
                      {() => (
                        <Table>
                          <thead>
                            <TableRow>
                              <TableHeadCell align="left">{t.screwFamilies.fields.substrates}</TableHeadCell>
                              <TableHeadCell align="left"></TableHeadCell>
                            </TableRow>
                          </thead>
                          <tbody>
                            {SUBSTRATE_CATEGORIES.map((category) => (
                              <TableRow key={category}>
                                <TableCell>{substrateCategoryLabels[category]}</TableCell>
                                <TableCell>
                                  <CompatibilityLevelEditor
                                    value={levelByCategory.get(category) ?? 0}
                                    onChange={(level) => updateLevel(category, level)}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </FormField>
                  );
                }}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ScrewFamilyForm;
